const BASE = "/api";

let getAuthToken: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(
  getter: (() => Promise<string | null>) | null,
) {
  getAuthToken = getter;
}

async function authHeaders(): Promise<Record<string, string>> {
  if (!getAuthToken) return {};
  try {
    const token = await getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function mergeHeaders(
  defaults: Record<string, string>,
  override?: HeadersInit | null,
): Record<string, string> {
  if (!override) return defaults;
  const merged = { ...defaults };
  if (override instanceof Headers) {
    override.forEach((v, k) => {
      merged[k] = v;
    });
  } else if (Array.isArray(override)) {
    for (const [k, v] of override) merged[k] = v;
  } else {
    Object.assign(merged, override);
  }
  return merged;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body != null;
  const defaultHeaders: Record<string, string> = {
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...(await authHeaders()),
  };
  const headers = mergeHeaders(defaultHeaders, init?.headers);
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

async function rawRequest(path: string, init?: RequestInit): Promise<Response> {
  const headers = mergeHeaders(await authHeaders(), init?.headers);
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res;
}

// ── Sell sheets (mirrors worker/services/sell-sheets.ts) ──────────────────
export interface SellSheetIndex {
  id: string;
  ticker: string;
  name: string;
  isComposite: boolean;
  halted: boolean;
}

export interface SellSheetRow {
  id: string;
  indexId: string;
  ticker: string;
  name: string;
  level: number | null;
  asOf: string;
  generatedBy: string;
  createdAt: string;
}

export interface NotionHubPage {
  id: string;
  title: string;
  lastEditedTime: string;
  icon?: string;
  categories: string[];
}

export interface PrInfo {
  /** Working branch the change landed on. */
  branch: string;
  /** Opened/updated pull request number, when one could be created. */
  number?: number;
  /** Pull request URL, when one could be created. */
  url?: string;
}

export interface GitCommitResult {
  sha: string;
  path: string;
  /** Present when the worker runs in PR mode (writes go to a PR, not main).
   *  For batched saves only `branch` is set (the PR is opened later). */
  pr?: PrInfo;
}

export interface PrBatchStatus {
  branch: string;
  /** Doc paths currently staged on the batch's draft branch. */
  files: string[];
  /** The open PR for this batch, if one has been opened. */
  pr: PrInfo | null;
}

export interface DocumentFamily {
  id: string;
  title: string;
  slug: { current: string } | string;
  docType: string;
  currentVersion?: { versionString: string } | string;
}

export interface DocumentVersion {
  id: string;
  title: string;
  versionString: string;
  effectiveDate: string;
  status: "draft" | "published" | "superseded";
  pdfUrl?: string;
}

export interface SanityDashboardFamily {
  id: string;
  title: string;
  slug: string | null;
  docType: "methodology" | "factsheet";
  currentVersion?: string;
  versions: Array<{
    versionString: string;
    status: "draft" | "published" | "superseded";
    effectiveDate?: string;
  }>;
}

export interface GitDocEntry {
  path: string;
  family: string;
  version: string;
  title: string;
  docType: string;
  status: string;
  /** Explicit public website slug from front matter (overrides the derived one). */
  slug?: string;
  effectiveDate?: string;
  nextReview?: string;
  supersedes?: string;
  notionPageId?: string;
  notionLastEditedTime?: string;
}

export interface Snippet {
  slug: string;
  title: string;
  category: string;
  description: string;
  content: string;
}

export interface SnippetInput {
  title: string;
  category: string;
  description: string;
  content: string;
}

export interface ConfigStatus {
  notion: boolean;
  sanity: boolean;
  sanityProject: string | null;
  sanityDataset: string | null;
  github: boolean;
  githubRepo: string | null;
  clerkAuth: boolean;
  pdfRendering: boolean;
}

export interface GitStatus {
  configured: boolean;
  connected: boolean;
  repo: string | null;
  url: string | null;
  defaultBranch: string | null;
  private?: boolean;
  docCount: number;
  error?: string;
}

// ── Reviews (mirrors worker/services/reviews.ts) ──────────────────────────

export type ReviewState = "assigned" | "signed" | "rejected";

export interface ReviewRow {
  doc_id: string;
  reviewer: string;
  state: ReviewState;
  assigned_at: string;
  signed_at: string | null;
}

// ── Suggestions / edit-proposing agent (M6) ───────────────────────────────

export type SuggestionStatus = "proposed" | "accepted" | "rejected";

export interface Suggestion {
  id: number;
  doc_id: string;
  anchor: string;
  before: string;
  after: string;
  rationale: string;
  status: SuggestionStatus;
  source: string;
  created_at: string;
}

// ── Bulk operations (mirrors worker/services/bulk-ops.ts) ─────────────────

export type BulkOpType =
  "rerender" | "publish" | "version-bump" | "terminology-fix";

export interface BulkJobLaunch {
  jobId: string;
  op: string;
  total: number;
  status: string;
  message?: string;
}

export interface BulkDocResult {
  docId: string;
  status: "done" | "skipped" | "failed";
  detail?: string;
}

export interface BulkJobStatus {
  jobId: string;
  op?: string;
  /** queued | running | complete | failed | errored | terminated */
  status: string;
  total?: number;
  processed?: number;
  done?: number;
  skipped?: number;
  failed?: number;
  results?: BulkDocResult[];
  createdAt?: string;
  updatedAt?: string;
}

/** Bulk job statuses that mean the workflow has stopped (no more polling). */
export const BULK_TERMINAL_STATUSES = [
  "complete",
  "failed",
  "errored",
  "terminated",
];

export interface AuditEntry {
  ts: string;
  action: string;
  message: string;
  /** Attributed actor (JWT sub, or "system"). Present on D1-sourced entries. */
  actor?: string;
  family?: string;
  version?: string;
  path?: string;
  sha?: string;
}

export type PdfMode = "browser-rendering" | "unknown";

// ── Templated families (mirrors worker/services/template-families.ts) ─────

export interface TemplateInstanceSpec {
  family: string;
  bindings: Record<string, string>;
  title?: string;
  version?: string;
}

export interface InstanceResult {
  family: string;
  path: string;
  status: "created" | "skipped" | "failed";
  detail?: string;
}

export interface InstantiateResult {
  templatePath: string;
  variables: string[];
  created: number;
  results: InstanceResult[];
}

export interface DriftLine {
  type: "same" | "add" | "del";
  text: string;
}

export interface DriftResult {
  docId: string;
  templateSource: string | null;
  drifted: boolean;
  changedLines: number;
  diff: DriftLine[];
  missingBindings: string[];
}

export const api = {
  me: () =>
    request<{ userId: string | null; role: string; org: string }>("/me"),
  config: {
    status: () => request<ConfigStatus>("/config/status"),
  },
  notion: {
    markdown: (id: string) =>
      request<{ markdown: string; unknownBlockIds?: string[] }>(
        `/notion/page/${id}/markdown`,
      ),
    hub: () =>
      request<{ pages: NotionHubPage[]; total: number }>("/notion/hub"),
    search: (q: string) =>
      request<{ pages: NotionHubPage[]; total: number }>(
        `/notion/search${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`,
      ),
    syncHub: () =>
      request<{ imported: number; errors?: string[] }>("/notion/sync-hub", {
        method: "POST",
      }),
    import: (pageId: string, batch?: string) =>
      request<{
        path: string;
        alreadyImported: boolean;
        title: string;
        staged?: boolean;
        pr?: PrInfo;
      }>("/notion/import", {
        method: "POST",
        body: JSON.stringify({ pageId, batch }),
      }),
  },
  git: {
    status: () => request<GitStatus>("/git/status"),
    tree: () => request<{ docs: GitDocEntry[] }>("/git/tree"),
    commit: (data: {
      path: string;
      content: string;
      message: string;
      sha?: string;
      action?: string;
      /** When set, the save stages onto the shared draft branch for this batch
       *  instead of opening a per-doc PR. */
      batch?: string;
    }) =>
      request<GitCommitResult>("/git/commit", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    prStatus: (batch: string) =>
      request<PrBatchStatus>(`/git/pr?batch=${encodeURIComponent(batch)}`),
    openPr: (data: { batch: string; title?: string; body?: string }) =>
      request<{ branch: string; pr: PrInfo; files: string[] }>("/git/pr", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    excludeFromBatch: (data: { batch: string; path: string }) =>
      request<{ branch: string; files: string[] }>("/git/pr/exclude", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    discardBatch: (data: { batch: string }) =>
      request<{ discarded: boolean }>("/git/pr/discard", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    log: (family: string) =>
      request<Array<{ sha: string; date: string; message: string }>>(
        `/git/log/${encodeURIComponent(family)}`,
      ),
    file: (path: string, ref?: string) => {
      const q = ref ? `?ref=${encodeURIComponent(ref)}` : "";
      return request<{ content: string; sha: string }>(
        `/git/file/${encodeURIComponent(path)}${q}`,
      );
    },
    commitPdf: (data: {
      path: string;
      message: string;
      pdf: Blob;
      batch?: string;
    }) => {
      const form = new FormData();
      form.append(
        "file",
        data.pdf,
        data.path.split("/").pop() ?? "document.pdf",
      );
      form.append("path", data.path);
      form.append("message", data.message);
      if (data.batch) form.append("batch", data.batch);
      return rawRequest("/git/commit-pdf", { method: "POST", body: form });
    },
  },
  audit: {
    list: (limit = 100) =>
      request<{ entries: AuditEntry[] }>(`/audit?limit=${limit}`),
  },
  snippets: {
    list: () => request<Snippet[]>("/snippets"),
    save: (slug: string, data: SnippetInput) =>
      request<{ slug: string; sha: string }>(
        `/snippets/${encodeURIComponent(slug)}`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        },
      ),
    remove: (slug: string) =>
      request<{ slug: string; deleted: boolean }>(
        `/snippets/${encodeURIComponent(slug)}`,
        {
          method: "DELETE",
        },
      ),
  },
  convert: {
    pdf: async (data: {
      template: string;
      title: string;
      version: string;
      content: string;
      metadata: Record<string, string>;
    }): Promise<{ response: Response; mode: PdfMode }> => {
      const response = await rawRequest("/convert/pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const header = response.headers.get("X-Shelf-Pdf-Mode");
      const mode: PdfMode =
        header === "browser-rendering" ? "browser-rendering" : "unknown";
      return { response, mode };
    },
  },
  bulk: {
    launch: (data: {
      op: BulkOpType;
      docIds: string[];
      idempotencyKey?: string;
      batch?: string;
    }) =>
      request<BulkJobLaunch>("/bulk", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    get: (id: string) =>
      request<BulkJobStatus>(`/bulk/${encodeURIComponent(id)}`),
  },
  agent: {
    propose: (docId: string) =>
      request<{ docId: string; suggestions: Suggestion[] }>(
        `/agent/propose/${encodeURIComponent(docId)}`,
        {
          method: "POST",
        },
      ),
    scan: (docIds?: string[]) =>
      request<{
        scanned: number;
        results: Array<{ docId: string; proposed: number; error?: string }>;
      }>("/agent/scan", {
        method: "POST",
        body: JSON.stringify(docIds ? { docIds } : {}),
      }),
  },
  suggestions: {
    forDoc: (docId: string, status?: SuggestionStatus) => {
      const qs = status ? `?status=${status}` : "";
      return request<{ docId: string; suggestions: Suggestion[] }>(
        `/suggestions/doc/${encodeURIComponent(docId)}${qs}`,
      );
    },
    accept: (id: number) =>
      request<{ suggestion: Suggestion }>(`/suggestions/${id}/accept`, {
        method: "POST",
      }),
    reject: (id: number) =>
      request<{ suggestion: Suggestion }>(`/suggestions/${id}/reject`, {
        method: "POST",
      }),
  },
  reviews: {
    queue: (reviewer?: string, state?: ReviewState) => {
      const sp = new URLSearchParams();
      if (reviewer) sp.set("reviewer", reviewer);
      if (state) sp.set("state", state);
      const qs = sp.toString();
      return request<{ reviewer: string; reviews: ReviewRow[] }>(
        `/reviews/queue${qs ? `?${qs}` : ""}`,
      );
    },
    forDoc: (docId: string) =>
      request<{ docId: string; reviews: ReviewRow[] }>(
        `/reviews/doc/${encodeURIComponent(docId)}`,
      ),
    assign: (docId: string, reviewer: string) =>
      request<{ docId: string; reviews: ReviewRow[] }>(
        `/reviews/${encodeURIComponent(docId)}/assign`,
        {
          method: "POST",
          body: JSON.stringify({ reviewer }),
        },
      ),
    sign: (docId: string) =>
      request<{ docId: string; reviews: ReviewRow[] }>(
        `/reviews/${encodeURIComponent(docId)}/sign`,
        {
          method: "POST",
        },
      ),
    reject: (docId: string) =>
      request<{ docId: string; reviews: ReviewRow[] }>(
        `/reviews/${encodeURIComponent(docId)}/reject`,
        {
          method: "POST",
        },
      ),
  },
  templateFamilies: {
    variables: (path: string) =>
      request<{ path: string; variables: string[] }>(
        `/templates/variables?path=${encodeURIComponent(path)}`,
      ),
    instantiate: (data: {
      templatePath: string;
      instances: TemplateInstanceSpec[];
    }) =>
      request<InstantiateResult>("/templates/instantiate", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    drift: (path: string) =>
      request<DriftResult>(`/templates/drift?path=${encodeURIComponent(path)}`),
  },
  sanity: {
    publish: (data: {
      familySlug: string;
      versionString: string;
      pdfFile: File;
      title: string;
      docType: string;
      effectiveDate: string;
      status: string;
      // Website Sanity type to publish as (methodology | factsheet | pdfDocument)
      // plus the optional Index/Rate Ticker the site keys methodology/factsheet on.
      targetType?: string;
      ticker?: string;
      // Explicit public website slug (overrides the family-derived one).
      slug?: string;
      supersedesVersion?: string;
      supersedesId?: string;
      changeLog?: string;
      materialChange?: boolean;
      noticeDate?: string;
      notice?: string;
      // Stage the git status→published change onto this batch's draft branch.
      batch?: string;
    }) => {
      const { pdfFile, ...meta } = data;
      const form = new FormData();
      form.append("pdf", pdfFile);
      form.append("metadata", JSON.stringify(meta));
      return rawRequest("/sanity/publish", { method: "POST", body: form });
    },
    unpublish: (data: {
      familySlug: string;
      docType: string;
      versionString: string;
      slug?: string;
    }) =>
      request<{
        success: boolean;
        deletedSiteDocs: string[];
        gitReverted: boolean;
      }>("/sanity/unpublish", { method: "POST", body: JSON.stringify(data) }),
    remove: (data: { familySlug: string }) =>
      request<{ success: boolean; deleted: number }>("/sanity/delete", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    families: () => request<DocumentFamily[]>("/sanity/families"),
    versions: (familyId: string) =>
      request<DocumentVersion[]>(`/sanity/versions/${familyId}`),
    dashboard: () =>
      request<{ families: SanityDashboardFamily[] }>("/sanity/dashboard"),
  },

  // ── Sell sheets (mirrors worker/services/sell-sheets.ts) ────────────────
  sellSheets: {
    indices: () =>
      request<{ indices: SellSheetIndex[] }>("/sell-sheets/indices"),
    list: (limit = 100) =>
      request<{ sheets: SellSheetRow[] }>(`/sell-sheets?limit=${limit}`),
    generate: (indexId: string) =>
      request<{ sheet: SellSheetRow }>("/sell-sheets", {
        method: "POST",
        body: JSON.stringify({ indexId }),
      }),
    /** Raw PDF bytes. The caller owns the object URL it creates from these. */
    pdf: (id: string, download = false) =>
      rawRequest(
        `/sell-sheets/${encodeURIComponent(id)}/pdf${download ? "?download=1" : ""}`,
      ),
  },
};
